import { Link } from "react-router-dom";
import type { EncounterDetail } from "../../../shared/types/encounter";
import {
  formatDate,
  formatPhone,
  genderLabel,
} from "../../../shared/format";
import { Badge } from "@/components/ui/badge";
import { encounterContextParts, encounterTimingLabel } from "../encounterContext";

interface Props {
  encounter: EncounterDetail;
  rightActions?: React.ReactNode;
}

const STATUS_PROPS = {
  // Nhãn trạng thái đồng bộ với SalePro (02/11/2026): Đang thực hiện / Hoàn thành / Đã hủy.
  IN_PROGRESS: { variant: "secondary" as const, label: "Đang thực hiện" },
  SIGNED: { variant: "success" as const, label: "Hoàn thành" },
  CANCELLED: { variant: "destructive" as const, label: "Đã hủy" },
};

/** Persistent header shared by every encounter sub-page — patient identity + key vitals at a glance. */
export function EncounterHeader({ encounter: e, rightActions }: Props) {
  const status = STATUS_PROPS[e.status];
  return (
    <header className="flex flex-col gap-4 rounded-lg border bg-card p-4 text-card-foreground shadow-sm sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary"
          aria-hidden="true"
        >
          {e.patientName
            .split(" ")
            .map((s) => s[0])
            .slice(-2)
            .join("")
            .toUpperCase()}
        </div>
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/patients/${e.patientId}`}
              className="text-base font-semibold text-foreground hover:underline"
            >
              {e.patientName}
            </Link>
            <Badge variant={status.variant}>{status.label}</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {e.patientCode && (
              <span className="font-mono">{e.patientCode}</span>
            )}
            {e.patientGender && <span>{genderLabel[e.patientGender]}</span>}
            {e.patientAge != null && <span>{e.patientAge} tuổi</span>}
            {e.patientPhone && <span>{formatPhone(e.patientPhone)}</span>}
            <span>
              BS: <strong className="font-medium text-foreground">{e.doctorName}</strong>
            </span>
            <span>{formatDate(e.startedAt)}</span>
          </div>
          {/* #19040 — dòng ngữ cảnh: ca này thuộc phòng nào, số mấy, khoa nào, và HAI mốc giờ.
              Trước đây đầu màn chỉ có danh tính bệnh nhân, nên bác sĩ mở nhầm ca của phòng khác
              mà không có dấu hiệu nào; ký xong là hồ sơ ghi sai người phụ trách. */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            {encounterContextParts(e).map((part, i) => (
              <span
                key={part}
                className={
                  i === 0 && !e.roomName
                    ? "rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900"
                    : "font-medium text-foreground"
                }
              >
                {part}
              </span>
            ))}
            {encounterTimingLabel(e.appointmentStartAt, e.startedAt) && (
              <span className="text-muted-foreground">
                {encounterTimingLabel(e.appointmentStartAt, e.startedAt)}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {e.vitals.recordedAt && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {e.vitals.systolicBp != null && (
              <span title="Huyết áp">
                {e.vitals.systolicBp}/{e.vitals.diastolicBp} mmHg
              </span>
            )}
            {e.vitals.heartRate != null && (
              <span title="Mạch">{e.vitals.heartRate} bpm</span>
            )}
            {e.vitals.temperatureC != null && (
              <span title="Nhiệt độ">{e.vitals.temperatureC}°C</span>
            )}
            {e.vitals.spO2 != null && (
              <span title="SpO₂">SpO₂ {e.vitals.spO2}%</span>
            )}
          </div>
        )}
        {rightActions}
      </div>
    </header>
  );
}
